import React, { useEffect, useState } from 'react';
import { useHistory, RouteComponentProps } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  NotaComercialApiResponse,
  CadastroNotaComercialApiRequest,
  DocumentDownloadInfo,
  InstrumentoFinanceiro,
  TipoRegistroInstrumentoFinanceiro,
  ReservarUltimoNumeroDaEmissaoRequest,
  GrupoAcesso,
} from 'common/genericTypes';
import NotaComercialParser from 'utils/parsers/notaComercialParser';
import { RootState } from 'store';
import { highlightErrors, typeOfRegister } from 'store/modules/notaComercial';
import { addAlert } from 'store/modules/snackBar';
import { reprocessarValidacao } from 'requests/divergencias';
import { updateId, resetEmissor } from 'store/modules/notaComercial/emissor';
import {
  resetMetadata,
  updateNotaComercialDetails,
  updateField,
  resetNotaComercialDetails,
} from 'store/modules/notaComercial/caracteristicas';
import {
  resetCriteriosRemuneracao,
  updateCriteriosRemuneracao,
} from 'store/modules/notaComercial/criteriosRemuneracao';
import { resetEventos, updateEventos } from 'store/modules/notaComercial/eventos';
import { resetDocuments, updateDocuments } from 'store/modules/notaComercial/uploadDocumentos';
import { UploadDocResponse } from 'store/modules/models/uploadDocResponse';
import Formulario from 'pages/notaComercial/registro/formulario';

type ComponentParams = { notaComercialId?: string };
type ComponentState = { 
  ncRascunho?: NotaComercialApiResponse; 
  acao?: TipoRegistroInstrumentoFinanceiro;
  instrumentoData?: {
    id: number;
    codigo: string;
    tipoInstrumentoFinanceiro: string;
    origem?: string;
    statusValidacao?: string;
    dataDeCriacao: Date;
  };
};

type Props = RouteComponentProps<ComponentParams, {}, ComponentState>;

const NotaComercial: React.FC<Props> = (props) => {
  const { notaComercialId } = props.match.params;
  const ncRascunho = props.location.state?.ncRascunho;
  const instrumentoData = props.location.state?.instrumentoData;

  const history = useHistory();
  const dispatch = useDispatch();

  const caracteristicas = useSelector((state: RootState) => state.notaComercialCadastro.caracteristicas);
  const participanteId = useSelector((state: RootState) => state.auth.usuario.participante.id);
  const usuarioGrupoAcesso = useSelector((state: RootState) => state.auth.usuario.grupoAcesso);
  const criteriosRemuneracao = useSelector((state: RootState) => state.notaComercialCadastro.criteriosRemuneracao);
  const eventos = useSelector((state: RootState) => state.notaComercialCadastro.eventos);
  const uploadDocumentos = useSelector((state: RootState) => state.notaComercialCadastro.uploadDocumentos);
  const emissor = useSelector((state: RootState) => state.notaComercialCadastro.emissor);

  const notaComercialApi = useSelector((state: RootState) => state.notaComercialApi);
  const instrumentoApi = useSelector((state: RootState) => state.instrumentoApi);

  const [codigo, setCodigo] = useState<string>('');
  const [reprocessamentoExecutado, setReprocessamentoExecutado] = useState<boolean>(false);

  const submitPutNotaComercial = async (
    notaComercialId: string,
    notaComercial: CadastroNotaComercialApiRequest
  ) => {
    let result = { codigo: '', ticket: '' };
    try {
      const res = await notaComercialApi.putNotaComercial(dispatch, notaComercialId, notaComercial, true);
      result = { codigo: res.codigo, ticket: res.ticket };
    } catch (error) { }
    return result;
  };

  const submitPostNotaComercial = async (): Promise<NotaComercialApiResponse> => {
    const notaComercial: CadastroNotaComercialApiRequest = NotaComercialParser.stateToCadastroNotaComercialApiRequest(
      caracteristicas,
      criteriosRemuneracao,
      eventos,
      uploadDocumentos,
      emissor
    );
    return await notaComercialApi.postNotaComercial(dispatch, notaComercial, true);
  };

  const submitNotaComercialDocument = async (
    notaComercialId: string,
    emissionType: string,
    file: File
  ): Promise<UploadDocResponse[]> => {
    try {
      return notaComercialApi.putUploadDoc(dispatch, notaComercialId, emissionType, file, true);
    } catch (error) {
      return [];
    }
  };

  const handleEditSubmit = async () => {
    if (!notaComercialId) return { codigo: '', ticket: '' };

    const notaComercial: CadastroNotaComercialApiRequest = NotaComercialParser.stateToCadastroNotaComercialApiRequest(
      caracteristicas,
      criteriosRemuneracao,
      eventos,
      uploadDocumentos,
      emissor
    );

    const result = await submitPutNotaComercial(notaComercialId, notaComercial);

    for (const document of uploadDocumentos.documents) {
      if (document.file) {
        const uploadResponse = await submitNotaComercialDocument(notaComercialId, document.type, document.file);
        if (!uploadResponse || uploadResponse.length === 0) return { codigo: '', ticket: '' };
      }
    }

    if (caracteristicas.id) {
      for (const documentToDelete of uploadDocumentos.documentsToDelete) {
        documentToDelete.remoteFileInfo?.id &&
          (await notaComercialApi.deleteUploadDoc(dispatch, notaComercialId, documentToDelete.remoteFileInfo.id, true));
      }
    }

    setReprocessamentoExecutado(true);
    if (instrumentoData && 
        instrumentoData.origem === 'FRONTIER' && 
        instrumentoData.statusValidacao === 'DIVERGENTE' &&
        usuarioGrupoAcesso === GrupoAcesso.Operacoes) {
      try {
        await reprocessarValidacao(dispatch, { 
          instrumentoFinanceiroId: instrumentoData.id.toString() 
        }, true);
      } catch (error) {
        console.error('Erro ao reprocessar validação:', error);
      }
    }

    return result;
  };

  const handleRegisterSubmit = async () => {
    const uploadedNotaComercial = await submitPostNotaComercial();

    if (uploadedNotaComercial.id) {
      for (const document of uploadDocumentos.documents) {
        if (document.file) {
          await submitNotaComercialDocument(uploadedNotaComercial.notaComercialId, document.type, document.file);
        }
      }
    }

    return { codigo: '', ticket: uploadedNotaComercial.ticket };
  };

  const handleAditamentoSubmit = async (): Promise<{ codigo: string, ticket: string } | undefined> => {
    try {
      if (codigo) {
        setReprocessamentoExecutado(true);
        if (instrumentoData && 
            instrumentoData.origem === 'FRONTIER' && 
            instrumentoData.statusValidacao === 'DIVERGENTE' &&
            usuarioGrupoAcesso === GrupoAcesso.Operacoes) {
          try {
            await reprocessarValidacao(dispatch, { 
              instrumentoFinanceiroId: instrumentoData.id.toString() 
            }, true);
          } catch (error) {
            console.error('Erro ao reprocessar validação:', error);
          }
        }
        
        return { codigo, ticket: '' };
      } else throw 'ID da Nota Comercial não identificado';
    } catch (error) {
      console.error(error);
    }
  };

  const handleReturnToList = () => {
    history.push('/solicitacoes');
  };

  const handleGerarTermoConstitutivo = async (): Promise<DocumentDownloadInfo> => {
    const notaComercial: CadastroNotaComercialApiRequest = NotaComercialParser.stateToCadastroNotaComercialApiRequest(
      caracteristicas,
      criteriosRemuneracao,
      eventos,
      uploadDocumentos,
      emissor
    );
    const termoConstitutivo = await notaComercialApi.postTermoConstitutivo(dispatch, notaComercial, true);
    return {
      url: termoConstitutivo.address,
      fileName: termoConstitutivo.fileName,
      extension: termoConstitutivo.extension,
    };
  };

  const handleSalvarRascunho = async () => {
    const numeroReservado = await reservarNumeroEmissao();
    if (!numeroReservado) return '';

    let draftId = '';
    try {
      const rascunho: CadastroNotaComercialApiRequest = NotaComercialParser.stateToCadastroNotaComercialApiRequest(
        caracteristicas,
        criteriosRemuneracao,
        eventos,
        uploadDocumentos,
        emissor
      );

      const documentsWithFiles = uploadDocumentos.documents
        .filter((doc) => !!doc.file)
        .map((doc) => ({ file: doc.file!, emissionType: doc.type }));

      const result = caracteristicas.draftId
        ? await instrumentoApi.putRascunho(
          dispatch,
          InstrumentoFinanceiro.NotaComercial,
          caracteristicas.draftId,
          rascunho,
          documentsWithFiles,
          true
        )
        : await instrumentoApi.postRascunho(
          dispatch,
          InstrumentoFinanceiro.NotaComercial,
          rascunho,
          documentsWithFiles,
          true
        );
      setCodigo(result.codigo);
      draftId = result.draftId;
      dispatch(updateField({ name: 'draftId', value: draftId }));

      dispatch(addAlert({ message: `Rascunho salvo com sucesso. Código do Instrumento Financeiro ${result.codigo}`, severity: 'success' }));
    } catch (error) { }
    return draftId;
  };

  const reserveBeforeHandleSubmit = async (handleSubmit: () => Promise<{ codigo: string; ticket: string } | undefined>): Promise<{ codigo: string; ticket: string } | undefined> => {

    const numeroEmissaoReservado = await reservarNumeroEmissao();
    if (!numeroEmissaoReservado) return;
    return await handleSubmit();
  }

  const reservarNumeroEmissao = async () => {
    if (!caracteristicas.numeroEmissao) {
      dispatch(addAlert({ message: 'Número de emissão não encontrado', severity: 'error' }));
      return false;
    }
    if (!caracteristicas?.cnpjEmissor) {
      dispatch(addAlert({ message: 'CNPJ do emissor não encontrado', severity: 'error' }));
      return false;
    }

    try {
      const reservaRequest: ReservarUltimoNumeroDaEmissaoRequest = {
        emissorCnpj: caracteristicas.cnpjEmissor,
        numeroProximaEmissao: caracteristicas.numeroEmissao,
        tipoInstrumento: InstrumentoFinanceiro.NotaComercial,
        externalId: participanteId,
      };

      await instrumentoApi.postReservarUltimoNumeroDaEmissao(dispatch, reservaRequest, true);
      return true;
    } catch (error) {
      dispatch(addAlert({ message: 'Erro ao reservar número de emissão', severity: 'error' }));
      return false;
    }
  };

  // Define Component Type
  const [typeOfFormulario, setTypeOfFormulario] = useState<TipoRegistroInstrumentoFinanceiro>(TipoRegistroInstrumentoFinanceiro.Cadastro);

  const defineTypeOfRegister = async (): Promise<void> => {
    if (notaComercialId) {
      if (props.location.state?.acao) {
        setTypeOfFormulario(props.location.state.acao);
        dispatch(typeOfRegister(props.location.state.acao));
      }
    } else {
      setTypeOfFormulario(TipoRegistroInstrumentoFinanceiro.Cadastro);
      dispatch(typeOfRegister(TipoRegistroInstrumentoFinanceiro.Cadastro));
    }
  };

  useEffect(() => {
    dispatch(resetEmissor());
    dispatch(resetNotaComercialDetails());
    dispatch(resetMetadata());
    dispatch(resetCriteriosRemuneracao());
    dispatch(resetEventos());
    dispatch(resetDocuments());
    dispatch(highlightErrors(false));
    defineTypeOfRegister();
  }, []);

  useEffect(() => {
    if (notaComercialId) {
      notaComercialApi
        .getNotaComercialDetalhes(dispatch, notaComercialId, true)
        .then((response) => {
          if (response) {
            setCodigo(response.codigo);
            dispatch(updateId(response.emissorId ?? ''));
            dispatch(updateNotaComercialDetails(NotaComercialParser.apiResponseToCaracteristicasState(response)));
            dispatch(updateCriteriosRemuneracao(NotaComercialParser.apiResponseToCriteriosRemuneracaoState(response)));
            dispatch(updateEventos(NotaComercialParser.apiResponseToEventosState(response)));
            dispatch(updateDocuments(NotaComercialParser.apiResponseToNotaComercialDocumentsState(response)));
          }
        })
        .catch(() => { });
    } else if (ncRascunho) {
      dispatch(updateId(ncRascunho.emissorId ?? ''));
      dispatch(updateNotaComercialDetails(NotaComercialParser.apiResponseToCaracteristicasState(ncRascunho, false)));
      dispatch(updateCriteriosRemuneracao(NotaComercialParser.apiResponseToCriteriosRemuneracaoState(ncRascunho)));
      dispatch(updateEventos(NotaComercialParser.apiResponseToEventosState(ncRascunho)));
      dispatch(updateDocuments(NotaComercialParser.apiResponseToNotaComercialDocumentsState(ncRascunho)));
    }
    defineTypeOfRegister();
  }, [notaComercialApi, notaComercialId, ncRascunho, dispatch]);

  const component = () => {
    switch (typeOfFormulario) {
      case TipoRegistroInstrumentoFinanceiro.Cadastro:
        return (
          <div>
            <Formulario
              title={`Cadastro Nota Comercial`}
              notaComercialId={notaComercialId}
              handleSubmit={() => reserveBeforeHandleSubmit(handleRegisterSubmit)}
              handleGerarTermoConstitutivo={handleGerarTermoConstitutivo}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit={false}
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.Edicao:
        return (
          <div>
            <Formulario
              title={`Editar Nota Comercial`}
              notaComercialId={notaComercialId}
              handleSubmit={handleEditSubmit}
              handleGerarTermoConstitutivo={handleGerarTermoConstitutivo}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.EdicaoEmitido:
        return (
          <div>
            <Formulario
              title={`Editar ${codigo}`}
              notaComercialId={notaComercialId}
              handleSubmit={handleAditamentoSubmit}
              handleGerarTermoConstitutivo={handleGerarTermoConstitutivo}
              handleReturn={() => history.push('/instrumentos/gerenciar')}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit
              codigo={codigo}
              instrumentoData={instrumentoData}
              reprocessamentoExecutado={reprocessamentoExecutado}
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.Aditamento:
        return (
          <div>
            <Formulario
              title={`Aditar Nota Comercial ${codigo}`}
              notaComercialId={notaComercialId}
              handleSubmit={handleAditamentoSubmit}
              handleGerarTermoConstitutivo={handleGerarTermoConstitutivo}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit
              codigo={codigo}
            />
          </div>
        );
      default:
        return <p>Loading</p>;
    }
  };
  return component();
};

export default NotaComercial;
